import dayjs from "dayjs";
import type { TFunction } from "i18next";
import type {
  LunchMenu,
  LunchMenuIn,
  LunchMenuSummary,
} from "../../../api/lunchMenus";

// Черновата на обедно меню в редактора и превръщането ѝ в заявка/текст.
// Дни от седмицата: 0 = неделя … 6 = събота (конвенцията на BE и org менютата).

export const ISO_DATE = "YYYY-MM-DD";
// Най-далечната дата на обедно меню — днес + 2 години (както BE MAX_YEARS_AHEAD)
export const MAX_YEARS_AHEAD = 2;
// Таван на обедната цена (както BE LunchMenuItemIn.price)
export const MAX_LUNCH_PRICE = 9999.99;
export const isValidLunchPrice = (price: string) => {
  const parsed = Number(price.replace(",", "."));
  return price.trim() !== "" && Number.isFinite(parsed) && parsed >= 0 && parsed <= MAX_LUNCH_PRICE;
};

// Порции на ден: цяло число 0…9 999 (0 = без ограничение) — BE `dailyLimit`
export const MAX_LUNCH_DAILY_LIMIT = 9999;
export const isValidLunchDailyLimit = (value: string) =>
  /^\d{1,4}$/.test(value.trim()) && Number(value) <= MAX_LUNCH_DAILY_LIMIT;

export const latestLunchDate = () => dayjs().add(MAX_YEARS_AHEAD, "year").format(ISO_DATE);
export const DISPLAY_DATE = "DD.MM.YYYY";
// Дните в реда на седмицата за бутоните (Пн … Нд)
export const WEEKDAYS_MONDAY_FIRST = [1, 2, 3, 4, 5, 6, 0];

export type LunchScheduleMode = "single" | "recurring";

export interface LunchDraftItem {
  itemId: number;
  price: string;
  // Размерът по езици, сменен само за обедното; празно → от продукта
  unitAndSizeLangs: Record<string, string>;
  // Порции на ден; 0 = без ограничение
  dailyLimit: number;
}

export interface LunchDraft {
  name: string;
  mode: LunchScheduleMode;
  dateFrom: string;
  dateTo: string | null;
  weekdays: number[];
  timeFrom: string;
  timeTo: string;
  items: LunchDraftItem[];
}

// „до" е до края на минутата: следващото меню започва в :00 без застъпване
const DEFAULT_TIME_FROM = "11:00";
const DEFAULT_TIME_TO = "14:59";

export const weekdayName = (t: TFunction, weekday: number) =>
  t(`lunchMenu.weekdayLong${weekday}`);

/** „Обяд · сряда · 24.09.2026" — вътрешното име по подразбиране (за списъка). */
export const defaultLunchName = (t: TFunction, isoDate: string) =>
  t("lunchMenu.defaultName", {
    day: weekdayName(t, dayjs(isoDate).day()),
    date: dayjs(isoDate).format(DISPLAY_DATE),
  });

export const newLunchDraft = (t: TFunction): LunchDraft => {
  const today = dayjs().format(ISO_DATE);
  return {
    name: defaultLunchName(t, today),
    mode: "single",
    dateFrom: today,
    dateTo: today,
    weekdays: [],
    timeFrom: DEFAULT_TIME_FROM,
    timeTo: DEFAULT_TIME_TO,
    items: [],
  };
};

/** Черновата на съществуващо меню; `asCopy` → ново меню от днес със същите продукти. */
export const lunchDraftFromMenu = (
  t: TFunction,
  menu: LunchMenu,
  asCopy: boolean,
): LunchDraft => {
  const recurring = (menu.weekdays?.length ?? 0) > 0 || menu.dateTo == null;
  const today = dayjs().format(ISO_DATE);
  const dateFrom = asCopy ? today : (menu.dateFrom ?? today);
  // Копие на меню за една дата с автоматично име („Обяд · вторник") получава
  // името на новия ден; ръчно име се запазва.
  const autoNamed =
    menu.dateFrom != null && menu.name === defaultLunchName(t, menu.dateFrom);
  return {
    name:
      asCopy && !recurring && autoNamed ? defaultLunchName(t, dateFrom) : menu.name,
    mode: recurring ? "recurring" : "single",
    dateFrom,
    dateTo: recurring ? (asCopy ? null : menu.dateTo) : dateFrom,
    weekdays: menu.weekdays ?? [],
    timeFrom: menu.timeFrom ?? DEFAULT_TIME_FROM,
    timeTo: menu.timeTo ?? DEFAULT_TIME_TO,
    items: menu.items.map((item) => ({
      itemId: item.itemId,
      price: item.price,
      unitAndSizeLangs: { ...(item.unitAndSizeLangs ?? {}) },
      dailyLimit: item.dailyLimit ?? 0,
    })),
  };
};

export const lunchDraftToPayload = (draft: LunchDraft): LunchMenuIn => ({
  name: draft.name.trim(),
  dateFrom: draft.dateFrom,
  dateTo: draft.mode === "single" ? draft.dateFrom : draft.dateTo,
  weekdays: draft.mode === "recurring" && draft.weekdays.length > 0 ? draft.weekdays : null,
  timeFrom: draft.timeFrom,
  timeTo: draft.timeTo,
  items: draft.items.map((item) => {
    const langs = Object.fromEntries(
      Object.entries(item.unitAndSizeLangs).filter(([, text]) => text.trim()),
    );
    return {
      itemId: item.itemId,
      price: Number(item.price).toFixed(2),
      unitAndSizeLangs: Object.keys(langs).length > 0 ? langs : null,
      dailyLimit: item.dailyLimit,
    };
  }),
});

/**
 * Първата грешка в черновата (ключ за превод) или null. `savedDateFrom` —
 * началната дата на редактираното меню: минала дата се допуска само ако не е
 * сменена (ново меню или нова дата → от днес нататък).
 */
export const lunchDraftError = (
  draft: LunchDraft,
  savedDateFrom: string | null,
): string | null => {
  if (!draft.name.trim()) return "lunchMenu.errorName";
  if (draft.dateFrom !== savedDateFrom && draft.dateFrom < dayjs().format(ISO_DATE))
    return "lunchMenu.errorPastDate";
  const latest = latestLunchDate();
  if (draft.dateFrom > latest || (draft.dateTo != null && draft.dateTo > latest))
    return "lunchMenu.errorFarDate";
  if (draft.items.length === 0) return "lunchMenu.errorItems";
  if (draft.timeFrom >= draft.timeTo) return "lunchMenu.errorTime";
  if (draft.mode === "recurring" && draft.weekdays.length === 0)
    return "lunchMenu.errorWeekdays";
  if (draft.mode === "recurring" && draft.dateTo && draft.dateTo < draft.dateFrom)
    return "lunchMenu.errorPeriod";
  if (draft.items.some((item) => !isValidLunchPrice(item.price)))
    return "lunchMenu.errorPrice";
  if (draft.items.some((item) => !isValidLunchDailyLimit(String(item.dailyLimit))))
    return "lunchMenu.errorDailyLimit";
  return null;
};

type ScheduleSource = Pick<
  LunchMenuSummary,
  "dateFrom" | "dateTo" | "weekdays" | "timeFrom" | "timeTo"
>;

const shortWeekdays = (t: TFunction, weekdays: number[]) =>
  WEEKDAYS_MONDAY_FIRST.filter((day) => weekdays.includes(day))
    .map((day) => t(`lunchMenu.weekdayShort${day}`))
    .join(", ");

/** „24.09.2026 · 11:30–15:00" или „всеки пн, ср · 11:30–14:30". */
export const lunchScheduleText = (t: TFunction, menu: ScheduleSource) => {
  const hours = menu.timeFrom && menu.timeTo ? `${menu.timeFrom}–${menu.timeTo}` : "";
  const singleDay = menu.dateFrom && menu.dateFrom === menu.dateTo && !menu.weekdays?.length;
  const days = singleDay
    ? dayjs(menu.dateFrom).format(DISPLAY_DATE)
    : menu.weekdays?.length
      ? t("lunchMenu.everyWeekdays", { days: shortWeekdays(t, menu.weekdays) })
      : t("lunchMenu.everyDay");
  return [days, hours].filter(Boolean).join(" · ");
};

/** „от 29.09.2026" / „29.09 – 30.10.2026" за повтарящо се меню; "" за една дата. */
export const lunchPeriodText = (t: TFunction, menu: ScheduleSource) => {
  if (!menu.dateFrom || (menu.dateFrom === menu.dateTo && !menu.weekdays?.length)) return "";
  const from = dayjs(menu.dateFrom).format(DISPLAY_DATE);
  return menu.dateTo
    ? `${from} – ${dayjs(menu.dateTo).format(DISPLAY_DATE)}`
    : t("lunchMenu.periodOpenEnded", { from });
};
